import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNotify } from 'react-admin';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Stack, Button, TextField, Switch, Alert, CircularProgress,
  InputAdornment, Link, Table, TableHead, TableRow, TableCell, TableBody,
} from '@mui/material';
import { card, btn, tbl, inputSx, typo, NAVY } from '../styles/ds';
import { websiteRequest, minorToInput, inputToMinor, selectionKey, isHiddurOnly } from './websiteApi';

/*
 * Prices and website rows of one listing, taken automatically from the CRM
 * (owner decision 2026-11-03). The server lists the CRM variants that match
 * the listing (its category's source products, script, and size / batim type);
 * the admin turns rows on and prices each price group:
 *   Simple — one price · Gasot — Size × Hiddur · Mezuzah — Hiddur.
 * Hand/Type never change a price, so their rows share their group's price.
 * Saving is the admin's confirmation of the CRM match (no separate verify step).
 * Variants that can't be shown (missing data, another size, duplicates…) are
 * listed with the reason, so the fix is made in the CRM.
 */

const HAND = { right: 'ימין', left: 'שמאל' };
const TYPE = { rashi: 'רש"י', rabbeinu_tam: 'רבנו תם' };
const HIDDUR_ORDER = ['standard', 'mehudar', 'premium'];

const WebsiteOfferEditor = ({ product, readOnly, onSaved }) => {
  const notify = useNotify();
  const [data, setData] = useState(null);          // { sources, rows } from the server
  const [shown, setShown] = useState(new Set());   // crm_variant_id of rows turned on
  const [priceInputs, setPriceInputs] = useState({});
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const hiddurLabel = code => product.options.find(o => o.key === 'hiddur')?.values.find(v => v.code === code)?.label_en || code;

  const load = useCallback(() => {
    websiteRequest(`/website_products/${product.id}/candidates`)
      .then(d => {
        setData(d);
        setShown(new Set(d.rows.filter(r => r.offered && !r.problem).map(r => r.crm_variant_id)));
        setPriceInputs(Object.fromEntries(product.prices.map(p => [selectionKey(p.price_key), minorToInput(p.amount_minor)])));
        setDirty(false);
      })
      .catch(err => notify(err.message, { type: 'error' }));
  }, [product, notify]);
  useEffect(() => { load(); }, [load]);

  // Price groups, in a stable order (size, then Hiddur level).
  const groups = useMemo(() => {
    if (!data) return [];
    const map = new Map();
    if (product.family === 'simple_tefillin') map.set(selectionKey({}), { key: {}, rows: [] });
    for (const r of data.rows.filter(x => x.price_key)) {
      const k = selectionKey(r.price_key);
      if (!map.has(k)) map.set(k, { key: r.price_key, rows: [] });
      map.get(k).rows.push(r);
    }
    const rank = g => [g.key.size ?? '', HIDDUR_ORDER.indexOf(g.key.hiddur ?? '')];
    return [...map.values()].sort((a, b) => {
      const [as, ah] = rank(a); const [bs, bh] = rank(b);
      return as.localeCompare(bs, 'en', { numeric: true }) || ah - bh;
    });
  }, [data, product.family]);

  if (!data) return <Card sx={card.section}><CardContent><Box p={3} textAlign="center"><CircularProgress size={28} /></Box></CardContent></Card>;

  const problems = data.rows.filter(r => r.problem);
  const groupTitle = key => {
    if (product.family === 'simple_tefillin') return 'מחיר קבוע למוצר';
    if (isHiddurOnly(product.family)) return hiddurLabel(key.hiddur);
    return `מידה ${key.size} · ${hiddurLabel(key.hiddur)}`;
  };
  const rowLabel = r => {
    const s = r.selections;
    if (isHiddurOnly(product.family)) return product.family === 'megillah' ? 'מגילה' : 'מזוזה';
    return [HAND[s.hand] ?? s.hand, TYPE[s.type] ?? s.type].join(' · ');
  };
  const disabled = readOnly || saving;
  const toggle = id => { setDirty(true); setShown(s => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; }); };
  const setPrice = (k, v) => { setDirty(true); setPriceInputs(p => ({ ...p, [k]: v })); };

  const save = async () => {
    const prices = [];
    const missing = [];
    for (const g of groups) {
      const needed = g.rows.some(r => shown.has(r.crm_variant_id));
      const amount = inputToMinor(priceInputs[selectionKey(g.key)]);
      if (needed && !amount) missing.push(groupTitle(g.key));
      if (amount && (needed || product.family === 'simple_tefillin')) prices.push({ selections: g.key, amount_minor: amount });
    }
    if (missing.length) { notify(`חסר מחיר תקין: ${missing.join(' · ')}`, { type: 'warning' }); return; }
    setSaving(true);
    try {
      const updated = await websiteRequest(`/website_products/${product.id}/offer`, 'PUT',
        { version: product.version, offered: [...shown], prices });
      notify('נשמר', { type: 'success' });
      onSaved(updated);
    } catch (err) { notify(err.message, { type: 'error' }); }
    finally { setSaving(false); }
  };

  return (
    <Card sx={card.section}>
      <CardContent className="crm-card-content" sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography sx={{ ...typo.sectionTitle, mb: 0.5 }}>מחירים ומוצרים מה-CRM</Typography>
        <Typography sx={{ ...typo.meta, mb: 2 }}>
          המערכת מציגה את הווריאנטים ב-CRM שמתאימים למוצר הזה. מסמנים מה מוצג באתר ומזינים מחיר אתר (USD, נפרד ממחירי ה-CRM)
          {product.family === 'simple_tefillin' && ' — מחיר אחד למוצר; יד ונוסח לא משנים מחיר.'}
          {product.family === 'gasot_tefillin' && ' — מחיר לכל מידה ורמת הידור; יד ונוסח לא משנים מחיר.'}
          {isHiddurOnly(product.family) && ' — מחיר לכל רמת הידור.'}
          {' '}השמירה היא גם האישור שההתאמה ל-CRM נכונה.
        </Typography>

        {data.sources.length === 0 ? (
          <Alert severity="warning">
            לקטגוריה הראשית של המוצר לא הוגדרו "מוצרי מקור ב-CRM", ולכן אין מאיפה לקחת וריאנטים. יש להגדיר אותם
            ב<Link component={RouterLink} to={`/website_categories/${product.category_id}`}>הגדרות הקטגוריה</Link>.
          </Alert>
        ) : (
          <>
            <Typography sx={{ ...typo.meta, mb: 2 }}>
              מוצרי מקור: {data.sources.map(s => s.name).join(', ')} · כתב <b dir="ltr">{product.script_code}</b>
              {product.fixed_selections?.size ? <> · מידה <b dir="ltr">{product.fixed_selections.size}</b></> : null}
            </Typography>
            {groups.length === 0 && problems.length === 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>לא נמצאו ב-CRM וריאנטים מתאימים למוצר הזה.</Alert>
            )}
            <Stack spacing={2}>
              {groups.map(g => {
                const k = selectionKey(g.key);
                return (
                  <Box key={k} sx={{ border: '1px solid rgba(13,29,62,0.12)', borderRadius: 2, p: { xs: 1.5, sm: 2 } }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap spacing={1.5} mb={g.rows.length ? 1 : 0}>
                      <Typography fontWeight={700} color={NAVY} dir="auto">{groupTitle(g.key)}</Typography>
                      <TextField size="small" label="מחיר אתר" value={priceInputs[k] ?? ''} disabled={disabled}
                        sx={{ ...inputSx, width: 170 }} inputProps={{ dir: 'ltr', inputMode: 'decimal', 'aria-label': `מחיר ${groupTitle(g.key)}` }}
                        InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                        onChange={e => setPrice(k, e.target.value)} />
                    </Stack>
                    {g.rows.map(r => (
                      <Stack key={r.crm_variant_id} direction="row" alignItems="center" spacing={1.5} py={0.5}
                        sx={{ borderTop: '1px solid rgba(13,29,62,0.06)' }}>
                        <Switch size="small" checked={shown.has(r.crm_variant_id)} disabled={disabled}
                          slotProps={{ input: { 'aria-label': `מוצג באתר: ${rowLabel(r)} (#${r.crm_variant_id})` } }}
                          onChange={() => toggle(r.crm_variant_id)} />
                        <Box minWidth={0}>
                          <Typography fontSize={14} fontWeight={600}>{rowLabel(r)}</Typography>
                          <Typography fontSize={12} color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                            #{r.crm_variant_id} · {r.variant_name}
                          </Typography>
                        </Box>
                        <Typography fontSize={12} sx={{ marginInlineStart: 'auto', whiteSpace: 'nowrap', color: shown.has(r.crm_variant_id) ? 'success.main' : 'text.secondary' }}>
                          {shown.has(r.crm_variant_id) ? 'מוצג באתר' : r.offered ? 'יוסר מהאתר' : 'לא מוצג'}
                        </Typography>
                      </Stack>
                    ))}
                  </Box>
                );
              })}
            </Stack>

            {problems.length > 0 && (
              <Box mt={3}>
                <Typography fontWeight={600} fontSize={14} mb={1}>וריאנטים שלא ניתן להציג — יש לתקן ב-CRM</Typography>
                <Box className="crm-table-scroll" data-mobile-scroll tabIndex={0} role="region" aria-label="וריאנטים שלא ניתן להציג" sx={tbl.container}>
                  <Table size="small">
                    <TableHead><TableRow><TableCell>וריאנט</TableCell><TableCell>סיבה</TableCell></TableRow></TableHead>
                    <TableBody>
                      {problems.map(r => (
                        <TableRow key={r.crm_variant_id}>
                          <TableCell sx={{ overflowWrap: 'anywhere' }}>#{r.crm_variant_id} · {r.variant_name}</TableCell>
                          <TableCell>
                            {r.problem.message}
                            {r.offered && <Typography fontSize={12} color="warning.dark">מוצג כרגע באתר — יוסר בשמירה הבאה</Typography>}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </Box>
            )}
          </>
        )}

        {!readOnly && data.sources.length > 0 && (
          <Stack direction="row" spacing={1.5} mt={2.5} alignItems="center" flexWrap="wrap" useFlexGap>
            <Button variant="contained" onClick={save} disabled={saving || !dirty} sx={btn.primary}>שמירה</Button>
            <Button variant="outlined" onClick={load} disabled={saving} sx={btn.cancel}>{dirty ? 'ביטול שינויים' : 'רענון מה-CRM'}</Button>
            {dirty && <Typography sx={typo.meta}>יש שינויים שלא נשמרו.</Typography>}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

export default WebsiteOfferEditor;
